import { useCallback, useEffect, useState } from "react";
import {
  hasValue,
  isEqualArrayIgnoringOrder,
  isNullish,
} from "../lib/util/common-util";
import { isEqual } from "lodash";

/**
 * 差分モードにおける、値の変化の有無を返却する。
 * そもそもdiffModeでなければfalseを返す。
 * diffModeであれば、変化ありならtrue, 変化なしならfalseを返す。
 */
export function useCheckEqualityInDiffMode<T extends {}>(
  isDiffMode: boolean,
  beforeValue: T | null | undefined,
  currentValue: T | null | undefined,
  /**
   * 2つの値が同じかどうかを判定する関数。
   * 基本的には省略可で、省略した場合は以下のデフォルトを用いる。
   * - プリミティブ値の場合: 単純な ===
   * - 配列の場合: 順序を無視した、要素単位の差分比較
   * - その他の場合: lodashのisEqual
   */
  checkEqualityFn: ((v0: T, v1: T) => boolean | Promise<boolean>) | undefined,
) {
  const [hasChangedInDiffMode, setHasChangedInDiffMode] = useState(false);

  const calculateHasChangedInDiffMode: () => Promise<boolean> =
    useCallback(async () => {
      if (!isDiffMode) return false;

      // 両方nullなら変化なし。
      if (isNullish(beforeValue) && isNullish(currentValue)) return false;

      // 両方はnullじゃないけど片方がnullなら変化あり。
      if (isNullish(beforeValue) || isNullish(currentValue)) return true;

      // checkEqualityFnが提供されているならそれを用いて結果を返す。
      if (hasValue(checkEqualityFn)) {
        const equalityFnResult = checkEqualityFn(beforeValue, currentValue);
        if (typeof equalityFnResult === "boolean") return !equalityFnResult;
        return !(await equalityFnResult);
      }

      // stringかnumberかbooleanなら単純比較の結果を返す。
      if (
        typeof beforeValue === "string" ||
        typeof beforeValue === "number" ||
        typeof beforeValue === "boolean" ||
        typeof currentValue === "string" ||
        typeof currentValue === "number" ||
        typeof currentValue === "boolean"
      ) {
        return beforeValue !== currentValue;
      }

      // 配列の場合、順序関係なく、全要素が一致するかを比較して返す。
      if (Array.isArray(beforeValue) && Array.isArray(currentValue)) {
        return !isEqualArrayIgnoringOrder(beforeValue, currentValue, true);
      }

      // その他の場合、deep equalityを単純比較して返す。
      return !isEqual(beforeValue, currentValue);
    }, [beforeValue, checkEqualityFn, currentValue, isDiffMode]);

  useEffect(() => {
    (async () => {
      const result = await calculateHasChangedInDiffMode();
      setHasChangedInDiffMode(result);
    })();
  }, [
    beforeValue,
    calculateHasChangedInDiffMode,
    checkEqualityFn,
    currentValue,
    isDiffMode,
  ]);

  return { hasChangedInDiffMode };
}
